import { accountType } from "@/shared/api/browser";
import { authClient, userClient } from "@/shared/rpc/clients";
import { Code, ConnectError } from "@connectrpc/connect";
import type { UserMessage } from "@cronika/proto/common/user_pb";
import type { AccountMessage } from "@cronika/proto/common/billing_pb";
import { makeAutoObservable, runInAction } from "mobx";
import { makePersistable } from "mobx-persist-store";
import { pairsStore } from "./PairsStore";
import { tradingProfileStore } from "./TradingProfileStore";
import { userSettingsStore } from "./UserSettingsStore";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

class UserStore {
  userFetching = true;
  loading = false;
  authorized = false;
  user: UserMessage | null = null;
  balance: AccountMessage | null = null;
  currentAccountType: "demo" | "real" | null = accountType.get();

  private refreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private balanceRefreshPromise: Promise<void> | null = null;
  private lastProcessedBalanceEventKey: string | null = null;

  constructor() {
    makeAutoObservable(this, {
      refreshIntervalId: false,
      balanceRefreshPromise: false,
      lastProcessedBalanceEventKey: false,
    } as any);

    makePersistable(this, {
      name: "UserStore",
      properties: ["authorized"],
      storage: localStorage,
    });

    accountType.subscribe((type) => {
      runInAction(() => {
        this.currentAccountType = type;
      });
    });
  }

  /**
   * Trading Balance for the real account = real + active bonus (Multi Balance Funding). This is
   * what the user trades with and sees as their balance; the real/bonus split is shown only on
   * the bonus page. Demo is a separate balance. Computed from the always-present real/bonus
   * fields (robust if the server's `tradingBalance` field isn't populated yet on first load).
   */
  get tradingBalance(): number {
    if (!this.balance) return 0;
    return Number(this.balance.tradingBalance);
  }

  get isZeroBalance(): boolean {
    if (!this.balance || !this.currentAccountType) return false;
    return this.currentAccountType === "real"
      ? this.tradingBalance <= 0
      : Number(this.balance.demoBalance) <= 0;
  }

  get currentBalance(): number | undefined {
    if (!this.balance || !this.currentAccountType) return undefined;
    return this.currentAccountType === "real"
      ? this.tradingBalance
      : Number(this.balance.demoBalance);
  }

  setUserData(userData: UserMessage) {
    this.applyUserData(userData);
    if (userData) {
      // Restore the server-stored account type for the signed-in user.
      void accountType.hydrate();
      pairsStore.fetchPairs();
      this.startRefreshInterval();
    }
  }

  private applyUserData(userData: UserMessage) {
    this.user = userData;
    this.authorized = true;
    if (userData.account) {
      this.balance = userData.account;
    }
  }

  async refreshProfile() {
    const data = await userClient.getProfile({});
    runInAction(() => {
      this.applyUserData(data);
    });
  }

  async getProfile() {
    try {
      const data = await userClient.getProfile({});
      runInAction(() => {
        this.setUserData(data);
      });
      // Preload user settings during the init splash so preference hooks
      // (e.g. useBetPreferences) can hydrate synchronously with no flash.
      await userSettingsStore.load();
    } catch {
      // handled by caller
    } finally {
      runInAction(() => {
        this.userFetching = false;
      });
    }
  }

  logout() {
    this.stopRefreshInterval();
    accountType.remove();

    this.user = null;
    this.balance = null;
    this.authorized = false;
    this.lastProcessedBalanceEventKey = null;
    pairsStore.reset();
    tradingProfileStore.resetForNewSession();
  }

  setBalance(balance: AccountMessage) {
    this.balance = balance;
  }

  async refreshBalance() {
    if (this.balanceRefreshPromise) {
      return this.balanceRefreshPromise;
    }

    this.balanceRefreshPromise = (async () => {
      try {
        const data = await userClient.getBalance({});
        runInAction(() => {
          this.balance = data;
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.balanceRefreshPromise = null;
      }
    })();

    return this.balanceRefreshPromise;
  }

  handleBetEvent(event: { type?: string; data?: { id?: string } } | null) {
    if (!event) return;
    // Only refresh on terminal events — bet.accepted is handled optimistically by the client and
    // triggering a server fetch here races with in-flight bets, causing stale balance overwrites.
    if (event.type !== "bet.settled" && event.type !== "bet.canceled") return;

    const eventId = event.data?.id ?? "unknown";
    const eventKey = `${event.type}:${eventId}`;
    if (this.lastProcessedBalanceEventKey === eventKey) return;

    this.lastProcessedBalanceEventKey = eventKey;
    this.refreshBalance();
  }

  async logoutAsync() {
    this.loading = true;
    try {
      await authClient.signOut({});
      runInAction(() => {
        this.logout();
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async signIn(credentials: { email: string; password: string }): Promise<UserMessage> {
    const response = await authClient.signIn(credentials);
    if (response.result.case !== "user" || !response.result.value) {
      throw new Error("Unexpected sign-in response");
    }
    await this.getProfile();
    return response.result.value;
  }

  async signUpAndLogin(
    signUpData: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
      country?: string;
      timezone?: string;
      language?: string;
      promoCode?: string;
      utmInfo?: {
        utmSource?: string;
        utmMedium?: string;
        utmCampaign?: string;
        utmContent?: string;
        utmTerm?: string;
        referrer?: string;
      };
    },
    signInData: { email: string; password: string },
  ): Promise<UserMessage> {
    await authClient.signUp(signUpData);
    const response = await authClient.signIn(signInData);
    if (response.result.case !== "user" || !response.result.value) {
      throw new Error("Unexpected sign-in response");
    }
    await this.getProfile();
    return response.result.value;
  }

  private startRefreshInterval() {
    this.stopRefreshInterval();
    this.refreshIntervalId = setInterval(async () => {
      try {
        const data = await authClient.refresh({});
        runInAction(() => {
          this.applyUserData(data);
        });
      } catch (e) {
        // Only logout on explicit auth rejection — transient network errors and
        // timeouts on slow connections must not terminate the session.
        const isAuthError = e instanceof ConnectError && e.code === Code.Unauthenticated;
        if (isAuthError) {
          runInAction(() => {
            this.logout();
          });
        }
      }
    }, REFRESH_INTERVAL_MS);
  }

  private stopRefreshInterval() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  }
}

export const userStore = new UserStore();
